<template>
  <video-js
    ref="player" data-embed="default" class="player"
    :class="[className]"
  />
</template>

<script>
/* eslint-disable no-underscore-dangle */
import cookies from 'js-cookie';

import { GTM_EVENTS } from '../utils/constants';

/**
 * Store player preferences in cookie
 */
const preferences = {
  set(k, v) {
    return cookies.set(`__player-${k}`, v, { expires: 30 });
  },
  get(k) {
    if (Array.isArray(k)) {
      return k.map((_k) => cookies.get(`__player-${_k}`));
    }

    return cookies.get(`__player-${k}`);
  },

  delete(k) {
    return cookies.remove(`__player-${k}`);
  },
};

/**
 * Open/Close control bar popup menus - captions, rates, qualities
 */
const toggleMenu = (e) => {
  e.preventDefault();
  document.querySelectorAll('.vjs-menu').forEach((m) => {
    if (!m.isSameNode(e.currentTarget.querySelector('.vjs-menu'))) {
      m.classList.remove('opened');
    }
  });

  e.currentTarget.querySelector('.vjs-menu').classList.toggle('opened');
};

const pushGtmEvent = (e) => dataLayer.push(e);

export default {
  props: {
    videoId: { type: String, required: true },
    className: { type: String, default: '' },
    // eslint-disable-next-line vue/no-boolean-default
    quality: { type: Boolean, default: true },
  },
  data: () => ({
    player: null,
    hideControlsTimeout: null,
    hidePlayPauseTimeout: null,
    sentGtmEvents: {
      25: false,
      50: false,
      75: false,
      100: false,
    },
    controlBarTimeout: 4000,
  }),
  beforeDestroy() {
    if (typeof videojs !== 'undefined') {
      videojs.deregisterPlugin('playFromBeginning');
      videojs.deregisterPlugin('autoplaySwitch');
      videojs.deregisterPlugin('rewindForward');
    }
  },
  async mounted() {
    this.$refs.player.setAttribute('data-account', this.$config.brightcove.accountId);
    this.$refs.player.setAttribute('data-player', this.$config.brightcove.playerId);
    this.$refs.player.setAttribute('data-video-id', this.videoId);

    /**
     * Register custom plugins
     */
    this.registerPlugins();

    /**
     * Initialize brightcove
     */
    bc(this.$refs.player);

    const _vm = this;
    videojs.getPlayer(this.$refs.player).ready(function ready() {
      _vm.player = this;

      if (this.quality) {
        this.qualityMenu({
          defaultResolution: preferences.get('quality'),
        });
      }

      // Call custom plugins
      this.playFromBeginning();
      this.rewindForward();

      const playVideo = () => {
        if (this.paused()) {
          this.play();
        }
      };

      _vm.$root.$on('video-play', playVideo);

      _vm.$once('hook:beforeDestroy', () => {
        _vm.$root.$off('video-play', playVideo);
      });

      this.on('loadedmetadata', _vm.loadedmetadata);

      this.on('volumechange', () => {
        if (this.muted()) {
          return preferences.set('volume', 0);
        }

        return preferences.set('volume', this.volume());
      });
      this.on('ratechange', () => {
        _vm.watched = 0;
        preferences.set('speed', this.playbackRate());
      });

      this.on('play-from-beginning', () => {
        this.currentTime(0);
      });

      this.on('timeupdate', _vm.$delayExecution(_vm.tracking, 900, this.paused(), _vm));
      this.on('play', () => pushGtmEvent(GTM_EVENTS.video.play));

      this.on('pause', () => pushGtmEvent(GTM_EVENTS.video.pause));

      this.on('ended', () => {
        setTimeout(() => pushGtmEvent(GTM_EVENTS.video.end), 100);
      });
    });
  },
  methods: {
    registerPlugins() {
      const _vm = this;
      videojs.registerPlugin('playFromBeginning', function playFromBeginning() {
        const controlBar = this.controlBar.el_;

        controlBar.insertAdjacentHTML('beforeend', '<button class="vjs-play-from-beginning vjs-control vjs-button"><span class="vjs-icon-placeholder"></span></button>');

        const dispatchPlayFromBeggining = () => this.trigger('play-from-beginning');

        const rewindButton = document.querySelector('.vjs-play-from-beginning');
        if (rewindButton) {
          rewindButton.addEventListener('click', dispatchPlayFromBeggining);
          rewindButton.addEventListener('touchstart', dispatchPlayFromBeggining, _vm.$eventPassive());
        }
      });

      videojs.registerPlugin('rewindForward', function rewindForward() {
        this.el_.addEventListener('keydown', (e) => {
          const currentTime = this.currentTime();
          if (e.code === 'ArrowLeft') {
            this.currentTime(currentTime - 10);
          } else if (e.code === 'ArrowRight') {
            this.currentTime(currentTime + 10);
          }
        });

        const controlBar = this.controlBar.el_;

        controlBar.insertAdjacentHTML('beforeend', `
          <button class="vjs-rewind vjs-control vjs-button">
            <span class="vjs-icon-placeholder"></span>
            <span class="vjs-control-text">-10s</span>
          </button>`);

        controlBar.insertAdjacentHTML('beforeend', `
          <button class="vjs-forward vjs-control vjs-button">
            <span class="vjs-icon-placeholder"></span>
            <span class="vjs-control-text">+10s</span>
          </button>`);

        controlBar.querySelector('.vjs-rewind').addEventListener('touchstart', () => {
          this.currentTime(this.currentTime() - 10);
        }, _vm.$eventPassive());

        controlBar.querySelector('.vjs-forward').addEventListener('touchstart', () => {
          this.currentTime(this.currentTime() + 10);
        }, _vm.$eventPassive());
      });
    },
    setupPreferences() {
      /**
       * Set saved preferences
       */

      const [volume, speed] = preferences.get(['volume', 'speed']);

      if (volume) {
        this.player.volume(volume);
      }

      if (speed) {
        this.player.playbackRate(speed);
      }
    },
    setupControlBarListeners() {
      /*
      |--------------------------------------------------------------------------
      | Click listeners
      |--------------------------------------------------------------------------
      |
      | Handle different actions happening in the video player
      |
      *

      /**
      * Actions happening in the control bar
      */
      const controlBar = this.player.controlBar.el_;

      if (this.quality) {
        const qualities = controlBar.querySelectorAll('.vjs-quality-menu-wrapper li');

        const selectQuality = (e) => {
          e.preventDefault();
          preferences.set('quality', e.currentTarget.querySelector('.vjs-menu-item-text').innerHTML);
        };

        if (qualities && qualities.length) {
          qualities.forEach((q) => {
            q.addEventListener('click', selectQuality);
            q.addEventListener('touchstart', selectQuality, this.$eventPassive());
          });
        }
      }

      const ratesButton = controlBar.querySelector('.vjs-playback-rate');
      const qualitiesButton = controlBar.querySelector('.vjs-quality-menu-wrapper');

      ratesButton.addEventListener('click', toggleMenu);
      ratesButton.addEventListener('touchstart', toggleMenu, this.$eventPassive());
      if (this.quality && qualitiesButton) {
        qualitiesButton.addEventListener('click', toggleMenu);
        qualitiesButton.addEventListener('touchstart', toggleMenu, this.$eventPassive());
      }

      const fadeOutPlayPauseButton = () => {
        document.querySelector('.vjs-big-play-button').classList.remove('fadeout');
        document.querySelector('.vjs-big-play-button').style.display = 'flex';
        setTimeout(() => document.querySelector('.vjs-big-play-button').classList.add('fadeout'), 200);

        clearTimeout(this.hidePlayPauseTimeout);
        this.hidePlayPauseTimeout = setTimeout(() => {
          document.querySelector('.vjs-big-play-button').style.display = 'none';
          document.querySelector('.vjs-big-play-button').classList.remove('fadeout');
        }, 900);
      };

      const allowOnlyAutoplaySwitch = (e) => {
        e.stopImmediatePropagation();
        const autoplayBtn = e.target.closest('.vjs-autoplay');
        if (autoplayBtn) {
          return;
        }
        e.preventDefault();
      };

      /**
      * Actions happening when click on video
      */
      const toggleControlsWithTimer = () => {
        if (this.player.el_.classList.contains('controls')) {
          this.player.el_.classList.toggle('controls');
        }
      };

      const onVideoClick = (e) => {
        e.preventDefault();
        this.player.el_.querySelectorAll('.vjs-menu').forEach((m) => m.classList.remove('opened'));

        if (this.$store.state.layout === 'desktop') {
          if (!this.player.paused()) {
            this.player.pause();
          } else {
            this.player.play();
          }

          fadeOutPlayPauseButton();
        } else {
          const t = e.currentTarget;
          setTimeout(() => {
            t.parentElement.classList.toggle('controls');

            // Hide controls after 5s
            clearTimeout(this.hideControlsTimeout);
            this.hideControlsTimeout = setTimeout(toggleControlsWithTimer, this.controlBarTimeout);
          }, 100);
        }
      };

      const video = this.player.el_.querySelector('video');
      video.addEventListener('click', onVideoClick);
      video.addEventListener('touchstart', onVideoClick, this.$eventPassive());

      if (this.$store.state.layout === 'desktop') {
        this.player.el_.addEventListener('mouseleave', () => {
          this.player.el_.querySelectorAll('.vjs-menu').forEach((m) => m.classList.remove('opened'));
        });

        const closeOpenedMenus = (e) => {
          allowOnlyAutoplaySwitch(e);

          const openedMenu = this.$refs.player.querySelector('.vjs-menu.opened');

          // close opened menus if click outside them
          if (openedMenu && !openedMenu.contains(e.target) && !ratesButton.contains(e.target) && !qualitiesButton.contains(e.target)) {
            document.querySelectorAll('.vjs-menu').forEach((m) => m.classList.remove('opened'));
          }
        };

        controlBar.addEventListener('click', closeOpenedMenus);
        controlBar.addEventListener('touchstart', closeOpenedMenus, this.$eventPassive());
      } else {
        const toggleControls = (e) => {
          clearTimeout(this.hideControlsTimeout);
          this.hideControlsTimeout = setTimeout(toggleControlsWithTimer, this.controlBarTimeout);

          allowOnlyAutoplaySwitch(e);

          if (e.target.classList.contains('vjs-control-bar')) {
            this.player.el_.classList.toggle('controls');
          }
        };

        controlBar.addEventListener('click', toggleControls);
        controlBar.addEventListener('touchstart', toggleControls, this.$eventPassive());
      }
    },
    loadedmetadata() {
      this.setupPreferences();
      this.setupControlBarListeners();
    },
    tracking() {
      if (this.player.paused()) {
        return;
      }

      const duration = this.player.duration();
      const part = duration / 4;

      const currentTime = this.player.currentTime();
      if (currentTime >= part && !this.sentGtmEvents[25]) {
        pushGtmEvent(GTM_EVENTS.video[25]);
        this.sentGtmEvents[25] = true;
      }

      if (currentTime >= part * 2 && !this.sentGtmEvents[50]) {
        pushGtmEvent(GTM_EVENTS.video[50]);
        this.sentGtmEvents[50] = true;
      }

      if (currentTime >= part * 3 && !this.sentGtmEvents[75]) {
        pushGtmEvent(GTM_EVENTS.video[75]);
        this.sentGtmEvents[75] = true;
      }
    },
  },
};
</script>

<style lang="scss">
.player.video-player.video-js {
  border-radius: 20px;
  height: auto;
  overflow: hidden;
  display: flex;

  &.new-play-button .vjs-big-play-button .vjs-icon-placeholder {
    width: 162px;
    height: 162px;
    transition: all var(--duration) ease;

    &:hover:after {
      background-color: rgba(44, 50, 58, 0.3);
    }

    &:after {
      content: "";
      width: 130px;
      height: 130px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(255, 255, 255, 0.02);
      border-radius: 50%;
      z-index: 0;
      transition: all var(--duration) ease;
    }

    &:before {
      width: 94px;
      height: 94px;
      left: 50%;
      border-radius: 50%;
      z-index: 1;
      background-image: url('../assets/svg/player/play-v2.svg');
      image-rendering: -webkit-optimize-contrast;
    }
  }

  .vjs-poster {
    background-size: cover;
  }

  .vjs-control-bar {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    .vjs-volume-panel {
      margin-left: auto !important;
    }
  }
}

@include bp($bp-mobile) {
  .player.video-player.video-js {
    border-radius: 6px;
    max-width: 100%;

    &.new-play-button .vjs-big-play-button .vjs-icon-placeholder {
      width: 62px !important;
      height: 62px !important;

      &:after {
        width: 50px;
        height: 50px;
      }

      &:before {
        width: 36px !important;
        height: 36px !important;
      }
    }

    .vjs-control-bar {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}
</style>

<style src="../assets/sass/player.scss" lang="scss" />'
